import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./document.module.css";
import i18n from "../../i18n";
import {
  checkProgramRestrictions,
  getPrintDate,
  getTimeFromTimestamp,
  isDateAfterToday,
} from "../../utils/utils";
import React, { ChangeEvent, useEffect, useRef, useState, useMemo } from "react";
import {
  useLazyGetMainPageInfoQuery,
  useUpdateTherapistTakingTreatmentPlanMutation,
  useGetAllAppointmentsInProgramMutation,
  useCancleAppointmentProgramMutation,
  useGetAllOpenTreatmentPlansMutation,
  usePostTreatmentPlanUpdatesMutation,
  useGetAllUpdatesForAppointmentProgramMutation,
} from "../../services/api/user.api";
import { useNavigate } from "react-router-dom";
import {
  TAppointment,
  TAppointmentProgramUpdate,
} from "../../services/types/user.types";
import { useAppSelector } from "../../services/hooks";
import {
  userSelector,
} from "../../services/selectors/user.selectors";
import Modal from "../modals/modal/Modal";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import profileIcon from "../../images/profile_icon.svg";
import { noDataLabel } from "../../utils/constants";
import { ITreatmentPlan } from "../../types/types";
import ProgramDatesPopup from "../modals/programDatesPopup/programDatesPopup";
import Loader from "../loader/loader";

interface IDocumentProps {
  openDocument: boolean;
  treatmentPlan: ITreatmentPlan;
  onClose: () => void;
  // The program's general notes are about a specific patient. Hide them where the
  // document is opened from a company-wide list of plans the therapist hasn't taken
  // yet; keep them where the plan is already the therapist's own.
  hideProgramComments?: boolean;
}
function TreatmentDocument({
  treatmentPlan,
  openDocument,
  onClose,
  hideProgramComments = false,
}: IDocumentProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const navigate = useNavigate();

  const therapist = useAppSelector(userSelector);


  const [refetchMainPageInfo] = useLazyGetMainPageInfoQuery();
  const [takeTreatmentPlan] = useUpdateTherapistTakingTreatmentPlanMutation();
  const [cancleAppointmentProgram] = useCancleAppointmentProgramMutation();
  const [getAllAppointmentsPrograms] = useGetAllOpenTreatmentPlansMutation();
  const [getAllAppointments, { isLoading }] =
    useGetAllAppointmentsInProgramMutation();
  const [postTreatmentPlanUpdates] = usePostTreatmentPlanUpdatesMutation();
  const [getAllUpdatesForAppointmentProgram] =
    useGetAllUpdatesForAppointmentProgramMutation();

  const [updates, setUpdates] = useState<TAppointmentProgramUpdate[]>([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openProgramDatesModal, setProgramDatesModal] = useState(false);
  const [programDates, setProgramDates] = useState<TAppointment[] | null>(null);
  const [textAreaValue, setTextAreaValue] = useState("");

  const [isMine, setIsMine] = useState<boolean>(
    treatmentPlan?.appointmentsProgram.therapistsGuid?.toUpperCase() === therapist.guid.toUpperCase()
  );

  const isGroupPlan = !!treatmentPlan.appointmentsProgram.treatmentGroupId || !!treatmentPlan.treatmentGroupName;

  const participants = useMemo(() => {
    if (treatmentPlan.users && treatmentPlan.users.length > 0) {
      return treatmentPlan.users;
    }
    if (treatmentPlan.appointmentsProgram.users && treatmentPlan.appointmentsProgram.users.length > 0) {
      return treatmentPlan.appointmentsProgram.users;
    }
    if (!programDates) return [];
    const uniqueUsers = new Map();
    programDates.forEach((app) => {
      if (app.users && !uniqueUsers.has(app.users.id)) {
        uniqueUsers.set(app.users.id, app.users);
      }
    });
    return Array.from(uniqueUsers.values());
  }, [programDates, treatmentPlan.appointmentsProgram.users, treatmentPlan.users]);


  useEffect(() => {
    const { usersId, therapistsId, users, ...restAppointmentsProgram } =
      treatmentPlan.appointmentsProgram;

    getAllAppointments(restAppointmentsProgram).then((res) => {
      if ("data" in res) {
        const mappedTimesInData = res.data.map((app) => {
          return {
            ...app.appointments,
            startTime: app.appointments.startTime
              ?.split(":")
              ?.slice(0, 2)
              ?.join(":"),
            endTime: app.appointments.endTime
              ?.split(":")
              ?.slice(0, 2)
              .join(":"),
            isReport: app.isReport,
          };
        });
        setProgramDates(mappedTimesInData);
      }
    });

    getAllUpdatesForAppointmentProgram(
      treatmentPlan.appointmentsProgram.id
    ).then((res) => {
      if ("data" in res) {
        setUpdates(res.data);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan, openDocument]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaValue(event.target.value);
  };

  const sendUpdate = (update: string) => {
    const creationDate = new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString();
    postTreatmentPlanUpdates({
      id: 0,
      usersGuid: treatmentPlan.appointmentsProgram.usersGuid,
      therapistsGuid: therapist.guid,
      comment: update,
      createOn: creationDate,
      appointmentsProgramsId: treatmentPlan.appointmentsProgram.id,
    })
      .then((res) => {
        if ("data" in res) setOpenSuccessModal(true);
        else setOpenErrorModal(true);
      })
      .catch(() => {
        setOpenErrorModal(true);
      })
      .finally(() => {
        refetchMainPageInfo();
        getAllUpdatesForAppointmentProgram(
          treatmentPlan.appointmentsProgram.id
        ).then((res) => {
          if ("data" in res) {
            setUpdates([...res.data]);
          }
        });
      });
  };

  const handleTakingClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const { usersId, therapistsId, ...restAppointmentsProgram } =
      treatmentPlan.appointmentsProgram;

    if (treatmentPlan) {
      takeTreatmentPlan({
        ...restAppointmentsProgram,
        therapistsSpecializationsIds: restAppointmentsProgram.therapistsSpecializationsIds?.length
          ? restAppointmentsProgram.therapistsSpecializationsIds
          : null,
        therapistsGuid: therapist.guid,
      })
        .then((res) => {
          // @ts-ignore
          if ("error" in res && res?.error?.originalStatus !== 200) {
            setOpenErrorModal(true);
          } else if ("data" in res) {
            // @ts-ignore
            if ("success" in res.data && res.data?.success === "false") {
              setOpenErrorModal(true);
              // Use API message if available, otherwise fall back to checkProgramRestrictions
              // @ts-ignore
              const apiMessage = res.data?.message;
              const message = apiMessage || t(checkProgramRestrictions(
                treatmentPlan.appointmentsProgram,
                therapist
              ));
              setErrorText(message);
            } else {
              setOpenSuccessModal(true);
              setProgramDates(res.data);
              setIsMine(true);
              getAllAppointmentsPrograms({
                companyId: therapist.companyId,
                therapistGuid: therapist.guid,
              });
            }
          }
        })
        .finally(() => {
          refetchMainPageInfo();
        });
    }
  };

  const cancleAppointment = () => {
    const { usersId, therapistsId, ...restAppointmentsProgram } =
      treatmentPlan.appointmentsProgram;

    cancleAppointmentProgram({ ...restAppointmentsProgram })
      .then((res) => {
        if ("data" in res) {
          setOpenSuccessModal(true);
          getAllAppointmentsPrograms({
            companyId: therapist.companyId,
            therapistGuid: therapist.guid,
          });
          onClose();
          setIsMine(false);
        } else {
          setOpenErrorModal(true);
        }
      })
      .finally(() => {
        refetchMainPageInfo();
      });
  };

  return (
    <article
      dir={i18n.dir()}
      className={`${styles.document} ${openDocument && styles.document_visible
        }`}
    >
      <button className={styles.documentCloseBtn} onClick={onClose} />
      <h4 className={styles.documentTitle}>
        {t("PLAN_NUMBER")} {treatmentPlan?.appointmentsProgram.id}
      </h4>
      <div className={styles.documentInfo}>
        <p className={styles.documentLabel}>{t('DOCUMENT_LABEL_OPEN_DATE')}<span className={styles.documentText}>
          {treatmentPlan.appointmentsProgram.createDate
            ? getPrintDate(
              new Date(treatmentPlan.appointmentsProgram.createDate)
            )
            : noDataLabel}
        </span>
        </p>
        <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_LABEL_TREATMENT_MODE')}<span className={styles.documentText}>
          {treatmentPlan?.mediaName || t("NOT_DEFINED")}
        </span>
        </p>

        <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_LABEL_SECTOR')}<span className={styles.documentText}>
          {treatmentPlan.migzarName
            || t("NO_PREFERENCE")}
        </span>
        </p>
        <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_LABEL_GENDER')}<span className={styles.documentText}>
          {treatmentPlan.genderName
            || t("NO_PREFERENCE")}
        </span>
        </p>
      </div>
      <div className={styles.documentLinks}>
        {!isGroupPlan && (<button
          onClick={() =>
            navigate(`/users/questions_reports`, {
              state: {
                UserDetails: isGroupPlan ? {
                  // For group report, we might need to pass group info.
                  // For now, we can pass the first user or handle it in actionReportPage.
                  // But since we are only implementing view, we can leave it or disable it.
                  // Or pass a dummy user if needed.
                  // Let's pass the first participant if available, or empty.
                  UsersId: participants[0]?.id,
                  FirstName: participants[0]?.firstName,
                  LastName: participants[0]?.lastName,
                  Phone: participants[0]?.phone,
                  Guid: participants[0]?.guid,
                } : {
                  UsersId: treatmentPlan.user?.id,
                  FirstName: treatmentPlan.user?.firstName,
                  LastName: treatmentPlan.user?.lastName,
                  Phone: treatmentPlan.user?.phone,
                  Guid: treatmentPlan.user?.guid,
                },
                TherapistDetails: {
                  TherapistQuestionnaireId: undefined, // Need to check what to pass
                  QuestionnaireCompaniesType: undefined,
                  QuestionnaireCompaniesId: undefined,
                  TherapistCycle: undefined,
                  QuestionnaireCompaniesTypeId: undefined,
                  appointmentsId: undefined,
                  treatmentPlanId: treatmentPlan.appointmentsProgram.id,
                }
              },
            })
          }
          className={`button button_white ${styles.btn} ${styles.btn_type_actionReport
            } ${styles.btn_type_actionReport_single}  ${!isMine && styles.btn_disabled
            }`}
          disabled={!isMine || (isGroupPlan && participants.length === 0)}
        >{t('DOCUMENT_BUTTON_QUESTIONNAIRE_REPORTS')}</button>)}
      </div>

      {isGroupPlan ? (
        <>
          <h5 className={styles.documentSubTitle}>{t('TREATMENT_PLAN_PARTICIPANTS')}</h5>
          {isLoading ? <Loader /> : participants.length > 0 ? (
            <ul className={styles.participantsList}>
              {participants.map((participant) => (
                <li 
                  key={participant.id} 
                  className={styles.participantItem}
                  onClick={isMine ? () => {
                    navigate(`/users/questions_reports`, {
                      state: {
                        UserDetails: {
                          UsersId: participant.id,
                          FirstName: participant.firstName,
                          LastName: participant.lastName,
                          Phone: participant.phone,
                          Guid: participant.guid,
                        },
                      },
                    })
                  } : undefined}
                >
                  <img src={profileIcon} alt="user" className={styles.participantIcon} />
                  <p className={styles.participantName}>
                    {participant.fullName}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.documentText}>{t('NO_PARTICIPANTS_FOUND')}</p>
          )}
        </>
      ) : (
        <>
          <h5 className={styles.documentSubTitle}>{t('DOCUMENT_SUBTITLE_CONTACT')}</h5>
          <p className={styles.documentLabel}>{t('DOCUMENT_LABEL_USER_NUMBER')}<span className={styles.documentText}>{treatmentPlan.user?.id}</span>
          </p>
          <p className={styles.documentLabel}>{t('DOCUMENT_LABEL_USER_NAME')}<span className={styles.documentText}>
            {treatmentPlan.user?.fullName || ""}
          </span>
          </p>
          {isMine && (
            <>
              <p className={styles.documentLabel}>{t('DOCUMENT_LABEL_PHONE')}<span className={styles.documentText}>
                {treatmentPlan.user?.phone}
              </span>
              </p>

              <p className={styles.documentLabel}>{t('DOCUMENT_LABEL_EMAIL')}<span className={styles.documentText}>
                {treatmentPlan.user?.email}
              </span>
              </p>
            </>
          )}
        </>
      )}
      {!hideProgramComments && treatmentPlan.appointmentsProgram.comments?.length !== 0 && (
    <div>
      <h5 className={styles.documentSubTitle}>{t('TREATMENT_DOCUMENT_GENERAL_NOTES_TITLE')}</h5>

      <div className={styles.updateInfo}>
        <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_LABEL_DATE')}<span className={styles.documentText}>
          {(treatmentPlan.appointmentsProgram.createDate &&
            `${getPrintDate(
              new Date(treatmentPlan.appointmentsProgram.createDate)
            )} ${getTimeFromTimestamp(
              new Date(treatmentPlan.appointmentsProgram.createDate)
            )}`) ||
            noDataLabel}
        </span>
        </p>
      </div>
      <p className={styles.documentContent}>
        {treatmentPlan.appointmentsProgram.comments}
      </p>
      <p className={styles.documentLabel}>
        {t("UPDATED_BY")} {treatmentPlan.createUserName}
      </p>
    </div>
      )}

      <h5 className={styles.documentSubTitle}>{t('ADD_TREATMENT_PLAN_LABEL_COMMENTS')}</h5>
      {updates && updates.length > 0 ? (
    <ul className={styles.documentUpdates}>
      {[...updates]
        .sort(
          (a: TAppointmentProgramUpdate, b: TAppointmentProgramUpdate) =>
            b.id - a.id
        )
        .map((upd) => (
          <li key={upd.id}>
            <div className={styles.updateInfo}>
              <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_UPDATE_LABEL')}<span className={styles.documentText}>{upd.id}</span>
              </p>
              <p className={styles.documentLabel}>{t('TREATMENT_DOCUMENT_LABEL_DATE')}<span className={styles.documentText}>
                {(upd.createOn &&
                  `${getPrintDate(
                    new Date(upd.createOn)
                  )} ${getTimeFromTimestamp(new Date(upd.createOn))}`) ||
                  noDataLabel}
              </span>
              </p>
            </div>
            <p className={styles.documentContent}>{upd.comment}</p>
            <p className={styles.documentLabel}>
              {t("UPDATED_BY")} {upd.therapistName}
            </p>
          </li>
        ))}
    </ul>
      ) : (
        <p className={styles.noDocuments}>{t('DOCUMENT_NO_UPDATES')}</p>
      )}
      <div className={styles.textAreaContainer}>
  <textarea
    ref={textAreaRef}
    rows={1}
    name={"updates"}
    placeholder={t('DOCUMENT_TEXTAREA_PLACEHOLDER')}
    className={`${styles.documentTextArea}`}
    onChange={handleChange}
    value={textAreaValue}
  />
  <button
    className={styles.sendUpdate}
    disabled={!textAreaValue}
    onClick={() => {
      setTextAreaValue("");
      sendUpdate(textAreaValue);
    }}
  />
      </div>
      {!isMine ? (
    <button
      disabled={isMine}
      className={`button button_white ${styles.btn} ${isMine && styles.btn_disabled
        }`}
      onClick={(e) => {
        handleTakingClick(e);
      }}
    >{t('DOCUMENT_BUTTON_TAKE')}</button>
      ) : (
        <>
          <button
      className={`button button_white ${styles.btn} ${styles.btn_full} `}
      onClick={() => setProgramDatesModal(true)}
    >{t('DOCUMENT_BUTTON_PROGRAM_DATES')}</button>
    {programDates &&
      programDates.length !== 0 &&
      programDates[0]?.appointmentDate &&
      isDateAfterToday(programDates[0].appointmentDate) && (
        <button
          className={`button button_white ${styles.btn} ${styles.btn_red} `}
          onClick={() => cancleAppointment()}
        >{t('DOCUMENT_BUTTON_CANCEL_PROGRAM')}</button>
      )}
        </>
      )}
      {openErrorModal && (
    <Modal
      onClose={() => {
        setOpenErrorModal(false);
      }}
    >
      <ErrorPopup
        text={errorText ?? null}
        onClose={() => {
          setOpenErrorModal(false);
        }}
      />
    </Modal>
      )}

      {openSuccessModal && (
    <Modal
      onClose={() => {
        setOpenSuccessModal(false);
      }}
    >
      <GeneralSuccessPopup
        onClose={() => {
          setOpenSuccessModal(false);
        }}
      />
    </Modal>
      )}

      {openProgramDatesModal && (
    <Modal
      prevencherAnswer
      onClose={() => {
        setProgramDatesModal(false);
      }}
    >
      <ProgramDatesPopup
        treatmentPlan={{
          ...treatmentPlan,
          appointmentsProgram: {
            ...treatmentPlan.appointmentsProgram,
            therapistsGuid: therapist.guid,
          },
        }}
        programDates={programDates}
        setProgramDates={setProgramDates}
        onClose={() => {
          setProgramDatesModal(false);
        }}
        onFullClose={() => {
          setProgramDatesModal(false);
          onClose();
        }}
        isLoading={!!isLoading}
      />
    </Modal>
      )}
    </article>
  );
}

export default TreatmentDocument;